import clsx from "clsx";
import { ReactElement, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  ApiParameterType,
  IntegrationAuthenticationType,
} from "../api/index.ts";
import { IntegrationMasterForm } from "../types/form-inputs.ts";
import { urlRegex } from "../utils/regex-definitions.ts";
import APIParameterTable from "./APIParameterTable.tsx";
import { Heading } from "./catalyst/Heading.tsx";
import ControlledRadioButtonGroup from "./inputs/ControlledRadioButtonGroup.tsx";
import InputFieldGroup from "./inputs/InputFieldGroup.tsx";
import SelectFieldGroup from "./inputs/SelectFieldGroup.tsx";
import { TabPanel, TabView } from "./TabView.tsx";

const HTTPIntegrationSettings = (): null | ReactElement => {
  const methods = useFormContext<IntegrationMasterForm>();

  const {
    control,
    formState: { errors },
    register,
    watch,
  } = methods;

  const authenticationType = watch("authenticationType");
  const [currentParameterType, setCurrentParameterType] =
    useState<string>("Header");

  const parameterTypes: string[] = Object.values(ApiParameterType).map(
    (value) => value,
  );

  let authFields = null;

  if (authenticationType === IntegrationAuthenticationType.API_KEY) {
    authFields = (
      <>
        <div className="sm:col-span-2">
          <InputFieldGroup<IntegrationMasterForm>
            errors={errors}
            fieldName="apiKeyName"
            label="API Key Name"
            placeHolder="MY_API_KEYNAME"
            register={register}
            rules={{
              required: "Please enter an API Key",
            }}
          />
        </div>
        <div className="sm:col-span-2">
          <InputFieldGroup<IntegrationMasterForm>
            errors={errors}
            fieldName="apiKey"
            label="API Key"
            placeHolder="MY_API_KEY"
            register={register}
            rules={{
              required: "Please enter an API Key",
            }}
            type="password"
          />
        </div>
      </>
    );
  } else if (authenticationType === IntegrationAuthenticationType.BASIC) {
    authFields = (
      <>
        <div className="sm:col-span-2">
          <InputFieldGroup<IntegrationMasterForm>
            errors={errors}
            fieldName="username"
            label="Username"
            placeHolder="MY_USERNAME"
            register={register}
            rules={{
              required: "Please enter your username",
            }}
          />
        </div>
        <div className="sm:col-span-2">
          <InputFieldGroup<IntegrationMasterForm>
            errors={errors}
            fieldName="password"
            label="Password"
            placeHolder="MY_PASSWORD"
            register={register}
            rules={{
              required: "Please enter a password",
            }}
            type="password"
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-6 flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-4 dark:border-white/10">
        <Heading level={2}>General Settings</Heading>
      </div>
      <div className="mt-10 grid grid-cols-6 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div className="sm:col-start- sm:col-span-2">
          <SelectFieldGroup<IntegrationMasterForm>
            errors={errors}
            fieldName="authenticationType"
            label="Authentication"
            options={Object.values(IntegrationAuthenticationType).map((v) => ({
              label: v,
              value: v,
            }))}
            register={register}
          />
        </div>
        {authFields}
        <div
          className={clsx(
            authenticationType !== IntegrationAuthenticationType.API_KEY &&
              "sm:col-span-6",
            authenticationType === IntegrationAuthenticationType.API_KEY &&
              "sm:col-span-4",
          )}
        >
          <div className="relative">
            <InputFieldGroup<IntegrationMasterForm>
              errors={errors}
              fieldName="baseUrl"
              label="Base URL"
              placeHolder="https://api.shodan.io"
              register={register}
              rules={{
                pattern: {
                  message: "Enter a valid URL",
                  value: urlRegex,
                },
                required: "URL is required",
              }}
              tooltip={{
                position: "right",
                text: "Enter the base endpoint URL for the API service",
              }}
              type="url"
            />
          </div>
        </div>
        {authenticationType === IntegrationAuthenticationType.API_KEY && (
          <div className="sm:col-span-2">
            <div className="flex flex-col gap-2">
              <ControlledRadioButtonGroup<IntegrationMasterForm>
                control={control}
                errors={errors}
                fieldName="apiKeyParameterType"
                label="API Key Mapping"
                optionList={parameterTypes}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mt-6">
        <TabView
          onTabChange={setCurrentParameterType}
          selectedTab={currentParameterType}
        >
          <TabPanel header="Header">
            <APIParameterTable currentParameterType={ApiParameterType.HEADER} />
          </TabPanel>
          <TabPanel header="Query Parameter">
            <APIParameterTable
              currentParameterType={ApiParameterType.QUERY_PARAMETER}
            />
          </TabPanel>
        </TabView>
      </div>
    </>
  );
};

export default HTTPIntegrationSettings;
